import React, { useEffect, useRef } from 'react';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TagIcon from '@square-icons/react/16/UI/tag';
import { getKioskSoftwareTranslationKey } from 'utilities/kiosk_software_copy';
import ProductHeading from 'components/pages/product/product_heading';
import ProductActions from 'components/pages/product/product_actions';
import ProductAlternatives from 'components/pages/product/product_alternatives';
import KioskMountSelector from 'components/pages/product/product_with_software_add_on/kiosk_mount_selector';
import ProductAddOn, {
  NO_ADD_ON_VALUE,
} from 'components/pages/product/product_add_on';
import ShippingBubble from 'components/pages/product/shipping_bubble';
import SubtotalBox from 'components/pages/product/subtotal_box';
import SoftwareAddOn from 'components/pages/product/product_with_software_add_on/software_add_on';
import {
  fetchSubscriptionState,
  fetchTrialEndDate,
  SOFTWARE_SUBSCRIPTION_STATES,
  pullSoftwareFromAddOns,
} from 'utilities/software';
import SoftwareAddOnBanner from 'components/pages/product/product_with_software_add_on/software_add_on_banner';
import RichTextContent from 'components/common/contentful/rich_text_content';
import { trackEvent, trackCdp } from 'utilities/tracking';
import CDP_EVENT_NAMES from 'typescript/constants/cdp_events';
import {
  checkAddOnsForActiveSubscription,
  extractSoftwareAddOns,
  sortProductAddOnSections,
  PRE_SELECTED_ADD_ONS,
  isKioskLiteAddOn,
} from 'components/pages/product/product_with_software_add_on/utils';
import {
  TRACKING_EVENT_TYPES,
  getAddOnByVariantId,
  getAlternativeUrl,
  setFooterDisclaimer,
} from 'components/pages/product/helpers';
import { updateCurrentUrl } from 'utilities/nav';
import ProductPromotionBanner from 'components/common/product_promotion_banner';
import InfoBubble from 'components/common/info_bubble';
import { useProductContext } from 'typescript/pages/product/contexts/product_context';
import { BASE_PRODUCT_KEYS } from 'constants/products';
import { useFeatureFlags } from 'components/context/feature_flags';
import { useProductOptionStyles } from '../product_options';
import ProductAddOnSkeleton from '../product_add_on_skeleton';

const HardwareWithSoftwareAddOnOptions = ({
  alternatives,
  currentAltMetadata,
  kioskMountMetadata,
  setFinancingModalOpen,
  setComparisonModalOpen,
  enableAlternativesModal = false,
  isKioskMountConfiguration = false,
}) => {
  const {
    product: canonicalProduct,
    currentProduct,
    setCurrentProduct,
    selection,
    setSelection,
    addOns,
    isGetAddOnsLoading: isAddOnsLoading,
    isGetAddOnsError: isAddOnsLoadingError,
    cart,
    currentUser,
    onUpdateItemQuantity,
    isUpdateItemQuantityLoading,
    scrollToRef,
    promoEligibility,
  } = useProductContext();

  const {
    base_product_key: baseProductKey,
    slug,
    name: productName,
    variant_token: variantToken,
    tracking_data: trackingData,
    shipping: {
      out_of_stock: outOfStock,
      no_ship: noShip,
      ship_quote: shipQuote,
      expedited_available: expeditedAvailable,
    },
    description,
    promotion_banner: promoBanner,
    add_ons_category_count: addOnsCategoryCount,
  } = currentProduct;

  const { number: orderNumber, item_count: itemCount, subtotal } = cart || {};
  const isK1A = canonicalProduct?.base_product_key === BASE_PRODUCT_KEYS.K1A;
  const sectionTitleClassName = isK1A ? 'kiosk-section-title' : 'medium-30';

  const { t, i18n } = useTranslation();
  const { s3bKioskSoftwareRenameToKioskLite: kioskLiteEnabled } =
    useFeatureFlags();
  const classes = useProductOptionStyles();
  const theme = useTheme();
  // Track the last product slug we pre-selected for to avoid re-running
  const preSelectedForSlugRef = useRef(null);

  // this useEffect is to update selections to include the requested add-ons upon page load
  // uses a mapping of product key to pre-selected add-ons, which is defined in the utils file
  // https://www.figma.com/design/pd4ELXLMyFOnE1SUT5vAb3/Shop---Kiosk-Launch?node-id=584-36132&t=gXIJZWecEJrFDIAZ-0
  useEffect(() => {
    if (isKioskMountConfiguration || isAddOnsLoading) return;
    // skip preselecting if we already pre-selected for this product
    if (preSelectedForSlugRef.current === slug) return;

    const preSelectedCategories = PRE_SELECTED_ADD_ONS[baseProductKey];

    // Build the new selection with pre-selected add-ons
    const newAddOns = {};
    if (preSelectedCategories && addOns && Object.keys(addOns).length > 0) {
      preSelectedCategories.forEach((category) => {
        const categoryAddOns = addOns[category];
        // Match SoftwareAddOn visibility so a hidden subscription is never
        // silently selected. Plus/Premium Kiosk device fees remain selectable.
        if (
          category.startsWith('software_') &&
          checkAddOnsForActiveSubscription(categoryAddOns || [], {
            isPnpSubscribed: Boolean(currentUser?.is_pnp_subscribed),
          })
        ) {
          return;
        }
        if (categoryAddOns && categoryAddOns.length > 0) {
          newAddOns[category] = categoryAddOns[0].variant_id.toString();
        }
      });
    }

    setSelection({ quantity: 1, addOns: newAddOns });
    preSelectedForSlugRef.current = slug;
  }, [
    slug,
    baseProductKey,
    addOns,
    isAddOnsLoading,
    isKioskMountConfiguration,
    currentUser?.is_pnp_subscribed,
    setSelection,
  ]);

  useEffect(() => {
    const software = pullSoftwareFromAddOns(addOns || {});
    const addedDisclaimers = new Set();

    software.forEach((addOn) => {
      // Product-specific footer first (e.g. Square Stand -> Kiosk Lite),
      // falling back to the software's default footer.
      const key = [
        (addOn.base_product_key !== 'sw-kiosk' ||
          (kioskLiteEnabled && isKioskLiteAddOn(addOn))) &&
          `productPage.software.disclaimer.${baseProductKey}.${addOn.base_product_key}`,
        `productPage.software.disclaimer.${addOn.base_product_key}`,
      ].find((k) => k && i18n.exists(k));
      const disclaimer =
        key && t(getKioskSoftwareTranslationKey(key, kioskLiteEnabled));
      if (
        disclaimer &&
        window.Sq?.SqShopFooter &&
        !window.Sq.SqShopFooter.disclaimers.includes(disclaimer)
      ) {
        setFooterDisclaimer(disclaimer);
        addedDisclaimers.add(disclaimer);
      }
    });

    return () => {
      if (!window.Sq?.SqShopFooter || addedDisclaimers.size === 0) return;
      window.Sq.SqShopFooter.disclaimers =
        window.Sq.SqShopFooter.disclaimers.filter(
          (disclaimer) => !addedDisclaimers.has(disclaimer),
        );
    };
  }, [baseProductKey, addOns, kioskLiteEnabled, i18n, t]);

  const onAlternativeSelected = (value) => {
    if (value === null) {
      return;
    }

    // Track alternative selected events
    const selectedAlternative = alternatives.find((alt) => alt.slug === value);
    trackEvent({
      name: 'Alternative',
      value: 'Change',
      params: {
        choice: selectedAlternative.name,
        alternativeUrl: getAlternativeUrl(selectedAlternative.slug),
        cartNumber: orderNumber,
        ...selectedAlternative.tracking_data,
      },
    });
    trackCdp({
      name: TRACKING_EVENT_TYPES.ChangeAlternative,
      params: {
        feature: 'shop',
        choice: selectedAlternative.name,
        alternativeUrl: getAlternativeUrl(selectedAlternative.slug),
        cartNumber: orderNumber,
        ...selectedAlternative.tracking_data,
      },
    });

    // Update current window path to selected alternative slug
    window.history.replaceState(
      {},
      null,
      getAlternativeUrl(selectedAlternative.slug),
    );

    // Update PublicWebNav current URL
    updateCurrentUrl(getAlternativeUrl(selectedAlternative.slug));

    setCurrentProduct(selectedAlternative);
    // Clear add-ons from the previous model. If the new model has no software
    // add-ons, this component unmounts (ProductOptions renders instead), so
    // a stale selection such as the pre-selected Kiosk Lite subscription would
    // otherwise be submitted with Add to Cart. Pre-selection re-runs per slug.
    setSelection({ quantity: 1, addOns: {} });
  };

  const onAddOnSelected = ({
    category: addOnCategory,
    newVariantId,
    choice,
  }) => {
    if (newVariantId === null) {
      return;
    }

    if (newVariantId === NO_ADD_ON_VALUE) {
      setSelection((prevSelection) => {
        const newAddOns = { ...prevSelection.addOns };
        delete newAddOns[addOnCategory];
        return { ...prevSelection, addOns: newAddOns };
      });
      return;
    } else {
      setSelection((prevSelection) => {
        const newAddOns = { ...prevSelection.addOns };
        newAddOns[addOnCategory] = newVariantId;
        return { ...prevSelection, addOns: newAddOns };
      });
    }

    const trackingData = getAddOnByVariantId(
      addOns,
      newVariantId,
    ).tracking_data;

    trackEvent({
      name: 'Add On',
      value: 'Change',
      params: {
        choice,
        cartNumber: orderNumber,
        ...trackingData,
      },
    });

    trackCdp({
      name: TRACKING_EVENT_TYPES.ChangeAddOn,
      params: {
        feature: 'shop',
        choice,
        cartNumber: orderNumber,
        ...trackingData,
      },
    });
  };

  const onAddToCartClick = () => {
    // GRW-816: this component replaces product_options.jsx for software-add-on
    // and S3B_KIOSK PDPs, so it needs the same body `click_link` event or those
    // surfaces' body ATC would go untracked while their sub-nav ATC fires.
    const atc = CDP_EVENT_NAMES.PRODUCT_PAGE.ADD_TO_CART_CLICK;
    trackCdp({
      name: atc.EVENT_NAME,
      params: {
        feature: 'shop',
        link_parent_id: atc.LINK_PARENT_ID_BODY,
        link_text: atc.LINK_TEXT,
        link_id: slug,
      },
    });

    onUpdateItemQuantity({
      variantToken,
      productName,
      quantity: selection.quantity,
      addOnIds: Object.values(selection.addOns),
      trackingData,
      source: 'pdp-hardware-with-software-add-on-options',
    });
  };

  const renderAddOns = () => {
    if (
      isAddOnsLoading ||
      // The request finished loading, but the data hasn't populated yet
      (!isAddOnsLoading &&
        !isAddOnsLoadingError &&
        Object.keys(addOns).length === 0 &&
        addOnsCategoryCount > 0)
    ) {
      return (
        <div
          style={{
            marginTop: theme.spacing(4),
            marginBottom: theme.spacing(4),
          }}
        >
          {Array.from({ length: addOnsCategoryCount }, (_, index) => (
            <div key={index} style={{ marginBottom: theme.spacing(2) }}>
              <ProductAddOnSkeleton />
            </div>
          ))}
        </div>
      );
    }

    const { softwareAddOnSections, hardwareAddOnSections } =
      sortProductAddOnSections(addOns);

    const addOnComponents = [];

    if (softwareAddOnSections.length > 0) {
      softwareAddOnSections.forEach((addOnSection) => {
        addOnComponents.push(
          <SoftwareAddOn
            titleClassName={sectionTitleClassName}
            key={addOnSection.category}
            category={addOnSection.category}
            addOns={addOnSection.addOns}
            selected={selection.addOns[addOnSection.category]}
            onSelect={onAddOnSelected}
            yesAsFirstOption={true}
            baseProductKey={baseProductKey}
          />,
        );
      });
    }

    if (hardwareAddOnSections.length > 0) {
      hardwareAddOnSections.forEach((addOnSection) => {
        addOnComponents.push(
          <ProductAddOn
            titleClassName={sectionTitleClassName}
            key={addOnSection.category}
            category={addOnSection.category}
            addOns={addOnSection.addOns}
            selected={selection.addOns[addOnSection.category]}
            onSelect={onAddOnSelected}
            yesAsFirstOption={true}
            currentProduct={currentProduct}
          />,
        );
      });
    }

    return <div className={classes.fadeIn}>{addOnComponents}</div>;
  };

  const renderSoftwareAddOnBanners = () => {
    const softwareAddOns = extractSoftwareAddOns(addOns);
    const isPnpSubscribed = Boolean(currentUser?.is_pnp_subscribed);

    if (softwareAddOns.length === 0) {
      return null;
    }

    // When the pricing selector is visible (i.e. no active subscription that
    // should suppress it), the banner is unnecessary.
    if (
      !checkAddOnsForActiveSubscription(softwareAddOns, { isPnpSubscribed })
    ) {
      return null;
    }

    return softwareAddOns.map((addOn) => {
      const subscriptionState = fetchSubscriptionState(addOn);
      const trialEndDate = fetchTrialEndDate(addOn);

      if (subscriptionState === SOFTWARE_SUBSCRIPTION_STATES.NOT_SUBSCRIBED) {
        return null;
      }

      return (
        <SoftwareAddOnBanner
          key={addOn.variant_id}
          softwareBaseProductKey={addOn.base_product_key}
          subscriptionState={subscriptionState}
          softwareName={addOn.name}
          trialEndDate={trialEndDate}
        />
      );
    });
  };

  const renderPromotionBanner = () => (
    <>
      <ProductPromotionBanner {...promoEligibility} slug={slug} />
      {promoBanner && (
        <InfoBubble icon={<TagIcon />}>
          <RichTextContent content={promoBanner} className="medium-20" />
        </InfoBubble>
      )}
    </>
  );

  return (
    <>
      <ProductHeading
        currentProduct={currentProduct}
        onFinancingTriggerClick={() => setFinancingModalOpen(true)}
      />
      <RichTextContent content={description} />
      {renderPromotionBanner()}
      {!isK1A &&
        (Object.keys(addOns).length > 0 || alternatives.length > 0) && (
          <h2
            className={classes.fadeIn}
            style={{ marginTop: theme.spacing(3.5) }}
          >
            {t('productPage.hardwareWithSoftwareHeader')}
          </h2>
        )}
      {renderSoftwareAddOnBanners()}
      <div className={classes.fadeIn}>
        {isKioskMountConfiguration ? (
          <KioskMountSelector
            alternatives={alternatives}
            canonicalAltMetadata={kioskMountMetadata}
          />
        ) : (
          <ProductAlternatives
            currentProduct={currentProduct}
            alternatives={alternatives}
            currentAltMetadata={currentAltMetadata}
            onAlternativeSelected={onAlternativeSelected}
            onComparisonTriggerClick={() => setComparisonModalOpen(true)}
            enableAlternativesModal={enableAlternativesModal}
          />
        )}
      </div>
      {renderAddOns()}
      <ProductActions
        currentProduct={currentProduct}
        addOns={addOns}
        selection={selection}
        setSelection={setSelection}
        requesting={isUpdateItemQuantityLoading}
        onAddToCartClick={onAddToCartClick}
      />
      <div
        style={{
          marginTop: theme.spacing(2.5),
          color: 'grey',
          fontSize: '14px',
          textAlign: 'center',
        }}
      >
        {t('productPage.software.softwareAddToCartDisclaimer')}
      </div>
      {!(outOfStock || noShip) && (
        <div
          data-testid="shipping-bubble-container"
          style={{ marginTop: theme.spacing(3.5) }}
        >
          <ShippingBubble
            quote={shipQuote}
            expeditedAvailable={expeditedAvailable}
          />
        </div>
      )}
      <div style={{ marginTop: theme.spacing(3.5) }} />
      <div ref={scrollToRef}>
        <SubtotalBox
          product={currentProduct}
          cart={cart}
          subtotal={subtotal}
          itemCount={itemCount}
        />
      </div>
    </>
  );
};

export default HardwareWithSoftwareAddOnOptions;
